/**
 * Local storage utility for persisting view states
 * Stores compact view mode and expanded nodes in browser's localStorage
 */

import { log } from './logger';

const STORAGE_KEYS = {
  COMPACT_VIEW: 'gantt_compact_view',
  EXPANDED_NODES: 'gantt_expanded_nodes',
  GANTT_VIEW_STATE: 'gantt_view_state',
} as const;

/**
 * Save compact view preference to local storage
 */
export function saveCompactViewState(isCompact: boolean): void {
  try {
    localStorage.setItem(STORAGE_KEYS.COMPACT_VIEW, JSON.stringify(isCompact));
  } catch (error) {
    log.warn('ViewStateStorage', 'Failed to save compact view state:', error);
  }
}

/**
 * Load compact view preference from local storage
 */
export function loadCompactViewState(): boolean | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.COMPACT_VIEW);
    if (stored !== null) {
      return JSON.parse(stored);
    }
  } catch (error) {
    log.warn('ViewStateStorage', 'Failed to load compact view state:', error);
  }
  return null;
}

/**
 * Save expanded node IDs to local storage
 */
export function saveExpandedNodesState(expandedNodeIds: Set<string>): void {
  try {
    const nodeArray = Array.from(expandedNodeIds);
    localStorage.setItem(STORAGE_KEYS.EXPANDED_NODES, JSON.stringify(nodeArray));
  } catch (error) {
    log.warn('ViewStateStorage', 'Failed to save expanded nodes state:', error);
  }
}

/**
 * Load expanded node IDs from local storage
 */
export function loadExpandedNodesState(): Set<string> | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.EXPANDED_NODES);
    if (stored !== null) {
      const nodeArray = JSON.parse(stored);
      if (Array.isArray(nodeArray)) {
        return new Set(nodeArray);
      }
    }
  } catch (error) {
    log.warn('ViewStateStorage', 'Failed to load expanded nodes state:', error);
  }
  return null;
}
